import type {ArenaState, DrawRecord, PeriodView, TokenSummary} from '@colosseum/shared';
import {api} from './api.js';
import {arenaFallback} from './chain.js';

/**
 * One poller for the numbers every view shares. Views subscribe and re-render
 * the parts that changed; nothing polls on its own.
 */

export interface AppState {
  arena: ArenaState | null;
  period: PeriodView | null;
  tokens: TokenSummary[];
  draws: DrawRecord[];
  /** 'live' when the indexer answers, 'chain' when only direct reads work, 'down' when neither does. */
  source: 'live' | 'chain' | 'down' | 'loading';
  lastError: string | null;
  updatedAt: number;
}

export const state: AppState = {
  arena: null, period: null, tokens: [], draws: [], source: 'loading', lastError: null, updatedAt: 0,
};

type Listener = (s: AppState) => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(): void {
  state.updatedAt = Date.now();
  for (const fn of listeners) fn(state);
}

let timer: ReturnType<typeof setInterval> | undefined;
let inFlight = false;

export async function refresh(): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  try {
    const [arena, period, tokens, draws] = await Promise.all([
      api.state(),
      api.period().catch(() => null),
      api.tokens({sort: 'fees', limit: 60}).catch(() => [] as TokenSummary[]),
      api.draws(12).catch(() => [] as DrawRecord[]),
    ]);
    state.arena = arena;
    state.period = period;
    state.tokens = tokens;
    state.draws = draws;
    state.source = 'live';
    state.lastError = null;
  } catch (e) {
    // The indexer is unreachable. Keep the countdown and the pot honest by
    // reading the chain directly, and say plainly that the rest is stale.
    const fallback = await arenaFallback();
    if (fallback) {
      state.source = 'chain';
      state.arena = {
        started: fallback.started,
        genesis: 0,
        currentPeriod: fallback.nextPeriod,
        nextPeriod: fallback.nextPeriod,
        nextClose: fallback.nextClose,
        potWei: fallback.potWei.toString(),
        round: '0',
        roundPublishTime: 0,
        drawable: false,
        pending: fallback.pending,
        qualifiedCount: state.arena?.qualifiedCount ?? 0,
        launchCount: state.arena?.launchCount ?? 0,
        launchesOpen: true,
      };
    } else {
      state.source = 'down';
    }
    state.lastError = (e as Error).message;
  } finally {
    inFlight = false;
    emit();
  }
}

export function startPolling(intervalMs = 5000): void {
  if (timer) return;
  void refresh();
  timer = setInterval(() => void refresh(), intervalMs);
  // A backgrounded tab does not need to poll; catch up when it comes back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });
}

export function stopPolling(): void {
  clearInterval(timer);
  timer = undefined;
}

/** Seconds left on the countdown, from the arena's own close time. */
export function secondsToClose(): number {
  if (!state.arena?.started) return 0;
  return Math.max(0, state.arena.nextClose - Math.floor(Date.now() / 1000));
}

/** True once the period has closed, its beacon exists and no purchase is pending, so anyone can settle it. */
export function drawIsCallable(): boolean {
  const a = state.arena;
  if (!a?.started || a.pending) return false;
  const now = Math.floor(Date.now() / 1000);
  return now >= a.nextClose && now >= a.roundPublishTime;
}
